import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { serviceOrderSummary } from "../../helpers/configRoutes/backend_helper";

const DEFAULT_STATS = {
    totalOrders: 0,
    openOrders: 0,
    overdueOrders: 0,
    serviceRequests: 0,
};

const OrderAndRequestService = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(DEFAULT_STATS);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const response = await serviceOrderSummary();

                if (response?.status && response?.stats) {
                    setStats({
                        totalOrders: Number(response.stats.totalOrders || 0),
                        openOrders: Number(response.stats.openOrders || 0),
                        overdueOrders: Number(response.stats.overdueOrders || 0),
                        serviceRequests: Number(response.stats.serviceRequests || 0),
                    });
                }
            } catch (error) {
                console.error("Error fetching maintenance services summary:", error);
            }
        };

        fetchSummary();
    }, []);

    return (
        <div className="min-h-screen bg-cs-surface">
            <div className="px-6 py-8 max-w-auto mx-auto space-y-8">
                <div>
                    <h1 className="text-2xl font-semibold text-cs-dark">
                        Maintenance services
                    </h1>
                    <p className="text-sm text-cs-muted mt-1">
                        Overview of maintenance activity and incoming requests.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                        label="Total orders"
                        value={stats.totalOrders}
                        icon="build"
                    />
                    <StatCard
                        label="Open orders"
                        value={stats.openOrders}
                        icon="pending_actions"
                        accent="warning"
                    />
                    <StatCard
                        label="Overdue"
                        value={stats.overdueOrders}
                        icon="error"
                        accent="danger"
                    />
                    <StatCard
                        label="Service requests"
                        value={stats.serviceRequests}
                        icon="report_problem"
                        accent="primary"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <NavCard
                        title="Service Orders"
                        description="Manage maintenance work for trucks and trailers."
                        icon="build"
                        onClick={() => navigate("/service-orders")}
                        footer={`${stats.totalOrders} total orders • ${stats.openOrders} open`}
                    />

                    <NavCard
                        title="Service Requests"
                        description="Review reported issues and maintenance requests."
                        icon="report_problem"
                        onClick={() => navigate("/services-request")}
                        footer={`${stats.serviceRequests} pending requests`}
                    />
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, icon, accent = "default" }) => {
    const accentMap = {
        default: "text-cs-dark",
        primary: "text-cs-primary",
        warning: "text-cs-warning",
        danger: "text-cs-danger",
    };

    return (
        <div className="bg-cs-bg border border-cs-border rounded-2xl p-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-cs-muted uppercase tracking-wider">
                    {label}
                </p>
                <span
                    className={`material-symbols-outlined ${accentMap[accent]}`}
                >
                    {icon}
                </span>
            </div>
            <p className="text-2xl font-semibold text-cs-dark mt-2">
                {value}
            </p>
        </div>
    );
};

const NavCard = ({ title, description, icon, footer, onClick }) => (
    <div
        onClick={onClick}
        className="
            cursor-pointer
            bg-cs-bg border border-cs-border rounded-2xl
            p-6
            hover:border-cs-primary
            hover:shadow-md
            transition-all
        "
    >
        <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-cs-primary-soft flex items-center justify-center">
                <span className="material-symbols-outlined text-cs-primary">
                    {icon}
                </span>
            </div>

            <div className="flex-1">
                <h2 className="text-lg font-semibold text-cs-dark">
                    {title}
                </h2>
                <p className="text-sm text-cs-muted mt-1">
                    {description}
                </p>

                <p className="text-xs text-cs-muted mt-4">
                    {footer}
                </p>
            </div>
        </div>
    </div>
);

export default OrderAndRequestService;
